<template lang="pug">
main.flex.min-h-screen.items-center.justify-center.bg-slate-100.px-4.py-8.font-sans.text-slate-950(
	class="sm:px-6"
	style="background: url('/static/b1_cp.png') top center / cover no-repeat;"
)
	.grid.w-full.max-w-5xl.overflow-hidden.rounded-3xl.bg-white.shadow-2xl(class="shadow-slate-900/10 md:min-h-[660px] md:grid-cols-[43%_57%]")
		section.relative.overflow-hidden.bg-blue-600.px-8.py-10.text-white(class="sm:px-12 md:flex md:flex-col md:justify-between md:px-11 md:py-12")
			.pointer-events-none.absolute.-left-28.-top-24.rounded-full.border(class="h-[340px] w-[340px] border-white/10")
			.pointer-events-none.absolute.-bottom-20.-right-16.rounded-full(class="h-[220px] w-[220px] bg-white/[0.04]")
			a.relative.text-2xl.font-extrabold.tracking-tight.text-white.no-underline(href="/" aria-label="Tiga AI home")
				| Tiga
				span(class="text-white/70") .ai

			.relative.mt-12(class="md:mt-0")
				p.mb-6.text-xs.font-semibold.tracking-wide(v-if="!isUnavailable" class="text-white/70") Step {{ activeIndex + 1 }} of 3
				p.mb-6.text-xs.font-semibold.tracking-wide(v-else class="text-white/70") Trial availability
				ol(v-if="!isUnavailable" aria-label="Signup progress")
					li.relative.flex.gap-4(
						v-for="(item, index) in steps"
						:key="item.id"
						class="min-h-[69px] last:min-h-0"
					)
						.absolute.top-9.border-l-2(v-if="index < 2" class="left-[17px] h-[33px] border-white/20")
						.relative.grid.h-9.w-9.flex-none.place-items-center.rounded-full.text-sm.font-bold.transition-all.duration-300(
							:class="index === activeIndex ? 'bg-white text-blue-600 ring-[5px] ring-white/20' : index < activeIndex ? 'bg-white text-blue-600' : 'bg-white/10 text-white/75'"
						)
							svg.h-5.w-5(v-if="index < activeIndex" viewBox="0 0 20 20" fill="currentColor")
								path(fill-rule="evenodd" d="M16.704 5.293a1 1 0 0 1 .003 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.411 0Z" clip-rule="evenodd")
							span(v-else) {{ index + 1 }}
						div
							p.leading-5(:class="index === activeIndex ? 'font-bold' : 'font-semibold'") {{ item.title }}
							p(class="mt-0.5 text-[13px] text-white/70") {{ index < activeIndex ? item.complete : item.description }}
			p.relative.mt-10(class="text-[13px] text-white/70 md:mt-0") {{ footerText }}

		section.flex.items-center.justify-center.px-7.py-12(class="sm:px-12 md:px-14")
			Transition(name="step" mode="out-in")
				.w-full.text-center(v-if="isUnavailable" key="unavailable" class="max-w-[400px]")
					.mx-auto.grid.h-14.w-14.place-items-center.rounded-full.bg-blue-50.text-blue-600
						svg.h-7.w-7(viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true")
							path(stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h7.5m-7.5 3h7.5m-7.5 3h4.5m-6 6.75h10.5A2.25 2.25 0 0 0 19.5 17.25V5.25A2.25 2.25 0 0 0 17.25 3H6.75A2.25 2.25 0 0 0 4.5 5.25v12A2.25 2.25 0 0 0 6.75 19.5Z")
					h1.mt-6.font-bold.leading-tight.tracking-tight(class="text-[26px]") Trial signups are currently full
					p.mt-3.leading-6.text-gray-500(class="text-[14.5px]") We’ve reached our current trial capacity. Please check back later.
					p.mt-7.text-gray-500(class="text-[13.5px]")
						| Already have an account? #[a.font-semibold.text-blue-600(href="/login" class="hover:underline") Log in]

				form.w-full(v-else-if="step === 'account'" key="account" class="max-w-[400px]" @submit.prevent="submitAccount")
					header
						h1.font-bold.leading-tight.tracking-tight(class="text-[26px]") Start your free trial
						p.mt-2.leading-6.text-gray-500(class="text-[14.5px]") Spin up your go-to-market workspace in under a minute.
					nav.mt-7.space-y-3(v-if="hasProviders" aria-label="Single sign-on options")
						a.flex.w-full.items-center.justify-center.gap-3.border.border-gray-300.bg-white.px-5.font-semibold.text-gray-800.no-underline.transition(
							v-if="googleEnabled"
							href="/auth/oidc/google?intent=signup"
							data-provider="google"
							class="h-[50px] rounded-[10px] text-[15.5px] hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-blue-600/15"
						)
							img.h-5.w-5(src="/static/google-signin.svg" alt="" aria-hidden="true")
							span Continue with Google
						a.flex.w-full.items-center.justify-center.gap-3.border.border-gray-300.bg-white.px-5.font-semibold.text-gray-800.no-underline.transition(
							v-if="microsoftEnabled"
							href="/auth/oidc/microsoft?intent=signup"
							data-provider="microsoft"
							class="h-[50px] rounded-[10px] text-[15.5px] hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-blue-600/15"
						)
							img.h-5.w-5(src="/static/microsoft-signin.svg" alt="" aria-hidden="true")
							span Continue with Microsoft
					.flex.items-center.gap-4.py-5(v-if="hasProviders" aria-hidden="true")
						.h-px.flex-1.bg-gray-200
						span.text-xs.font-semibold.tracking-wider.text-gray-400 OR USE YOUR EMAIL
						.h-px.flex-1.bg-gray-200
					label.block(:class="hasProviders ? '' : 'mt-7'")
						span.sr-only Work email
						input.w-full.border-gray-300.px-4.shadow-none(v-model.trim="email" type="email" autocomplete="email" placeholder="you@company.com" required class="h-[50px] rounded-[10px] text-[15.5px] placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600")
					ErrorMessage(:message="error")
					SubmitButton.mt-5(:loading="loading" loading-label="Sending code…") Continue with email
					p.mt-4.text-gray-400(class="text-[12.5px] leading-[18.75px]")
						| By continuing you agree to Tiga’s #[a.text-gray-500(href="https://www.tiga.ai/master-agreement" class="hover:underline") Terms] &amp; #[a.text-gray-500(href="https://www.tiga.ai/privacy-policy" class="hover:underline") Privacy Policy].
					p.mt-7.text-gray-500(class="text-[13.5px]")
						| Already have an account? #[a.font-semibold.text-blue-600(href="/login" class="hover:underline") Log in]

				form.w-full(v-else-if="step === 'verify'" key="verify" class="max-w-[400px]" @submit.prevent="submitCode")
					header
						h1.font-bold.leading-tight.tracking-tight(class="text-[26px]") Verify your email
						p.mt-2.leading-6.text-gray-500(class="text-[14.5px]")
							| Enter the verification code we sent
							template(v-if="email")  to #[strong.font-semibold.text-gray-600 {{ email }}]
							| .
					fieldset.mt-7
						legend.sr-only Verification code
						.grid.grid-cols-6.gap-2.px-8
							input.h-12.w-full.min-w-0.rounded-lg.border-gray-300.p-0.text-center.font-mono.text-lg.font-bold.uppercase.shadow-none(
								v-for="(_, index) in code"
								:key="index"
								:ref="element => setCodeInput(element, index)"
								v-model="code[index]"
								type="text"
								maxlength="1"
								:autocomplete="index === 0 ? 'one-time-code' : 'off'"
								:aria-label="`Verification code character ${index + 1}`"
								@input="onCodeInput(index, $event)"
								@keydown="onCodeKeydown(index, $event)"
								@paste.prevent="onCodePaste(index, $event)"
								class="focus:border-blue-600 focus:ring-blue-600"
							)
					ErrorMessage(:message="error")
					SubmitButton.mt-5(:loading="loading" loading-label="Verifying…") Verify email
					p.mt-7.text-gray-500(class="text-[13.5px]")
						| Used the wrong email? #[button.font-semibold.text-blue-600(type="button" :disabled="loading" @click="startOver" class="hover:underline disabled:cursor-wait disabled:opacity-60") {{ loading ? "Starting over…" : "Start over" }}]

				form.w-full(v-else key="profile" class="max-w-[400px]" @submit.prevent="submitProfile")
					header
						h1.font-bold.leading-tight.tracking-tight(class="text-[26px]") Complete your profile
						p.mt-2.leading-6.text-gray-500(class="text-[14.5px]") Tell us a little about you and your company.
					.grid.mt-7.grid-cols-1.gap-3(class="sm:grid-cols-2")
						label.block
							span.sr-only First name
							input.w-full.border-gray-300.px-4.shadow-none(v-model.trim="firstName" type="text" autocomplete="given-name" maxlength="255" placeholder="First name" required class="h-[50px] rounded-[10px] text-[15.5px] placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600")
						label.block
							span.sr-only Last name
							input.w-full.border-gray-300.px-4.shadow-none(v-model.trim="lastName" type="text" autocomplete="family-name" maxlength="255" placeholder="Last name" required class="h-[50px] rounded-[10px] text-[15.5px] placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600")
					label.mt-3.block
						span.sr-only Company name
						input.w-full.border-gray-300.px-4.shadow-none(v-model.trim="companyName" type="text" autocomplete="organization" maxlength="255" placeholder="Company name" required class="h-[50px] rounded-[10px] text-[15.5px] placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600")
					ErrorMessage(:message="error")
					SubmitButton.mt-5(:loading="loading" loading-label="Finishing setup…") Finish setup
</template>

<script>
import SubmitButton from "./components/SubmitButton.vue"
import ErrorMessage from "./components/ErrorMessage.vue"
import { storedUtmParams } from "./utm.js"

export default {
	components: { SubmitButton, ErrorMessage },
	props: {
		initialStep: { type: String, default: "account" },
		initialEmail: { type: String, default: "" },
		initialFirstName: { type: String, default: "" },
		initialLastName: { type: String, default: "" },
		initialError: { type: String, default: "" },
		googleEnabled: { type: Boolean, default: true },
		microsoftEnabled: { type: Boolean, default: true },
	},
	data () {
		return {
			step: ["account", "verify", "profile", "unavailable"].includes(this.initialStep) ? this.initialStep : "account",
			email: this.initialEmail,
			firstName: this.initialFirstName,
			lastName: this.initialLastName,
			companyName: "",
			code: Array(6).fill(""),
			codeInputs: [],
			loading: false,
			error: this.initialError
		}
	},
	computed: {
		steps () { return [{ id: "account", title: "Account", description: "Your work email", complete: "Email added" }, { id: "verify", title: "Verify", description: "Confirm it’s you", complete: "Confirmed" }, { id: "profile", title: "Profile", description: "Tell us your name", complete: "Complete" }] },
		hasProviders () { return this.googleEnabled || this.microsoftEnabled },
		isUnavailable () { return this.step === "unavailable" },
		activeIndex () { return this.steps.findIndex(item => item.id === this.step) },
		footerText () { return this.isUnavailable ? "Thanks for your interest in Tiga." : this.step === "account" ? "Takes about a minute." : this.step === "verify" ? "Check your inbox for your code." : "You’re almost there." }
	},
	methods: {
		async request (url, values) {
			this.loading = true
			this.error = ""
			try {
				const response = await this.axios.post(url, new URLSearchParams(values), { headers: { "Content-Type": "application/x-www-form-urlencoded" } })
				return response.data
			} catch (error) {
				this.error = error.response?.data?.error || "Something went wrong. Please try again."
				return null
			} finally { this.loading = false }
		},
		async submitAccount () { const data = await this.request("/signup", { email: this.email }); if (data?.next === "verify") { this.changeStep("verify") } },
		async submitCode () { const value = this.code.join(""); if (value.length !== 6) { this.error = "Enter all six characters."; return } const data = await this.request("/signup/verify", { code: value }); if (data?.next === "profile") { this.applyProfilePrefill(data); this.changeStep("profile") } },
		async submitProfile () { const data = await this.request("/signup/profile", { first_name: this.firstName, last_name: this.lastName, company_name: this.companyName, ...storedUtmParams() }); if (data?.agent_id) { this.redirectToAgent(data.agent_id) } },
		applyProfilePrefill (data) { this.firstName ||= data?.first_name || ""; this.lastName ||= data?.last_name || "" },
		redirectToAgent (agentID) { window.location.assign(`/agent#/agents/${encodeURIComponent(agentID)}`) },
		changeStep (step) { this.error = ""; this.step = step; window.history.replaceState({}, "", step === "account" ? "/signup" : `/signup?step=${step}`); if (step === "verify") { this.$nextTick(() => this.codeInputs[0]?.focus()) } },
		async startOver () {
			this.loading = true
			this.error = ""
			try {
				await this.axios.delete("/signup")
				this.email = ""
				this.firstName = ""
				this.lastName = ""
				this.companyName = ""
				this.code = Array(6).fill("")
				this.changeStep("account")
			} catch (error) {
				this.error = error.response?.data?.error || "We couldn’t restart signup. Please try again."
			} finally { this.loading = false }
		},
		setCodeInput (element, index) { if (element) { this.codeInputs[index] = element } },
		onCodeInput (index, event) { const value = event.target.value.replace(/\s/g, "").toUpperCase(); if (value.length > 1) { return this.fillCode(index, value) } this.code[index] = value; if (value && index < 5) { this.codeInputs[index + 1]?.focus() } this.submitCodeIfComplete() },
		onCodeKeydown (index, event) { if (event.key === "Backspace" && !this.code[index] && index > 0) { this.code[index - 1] = ""; this.codeInputs[index - 1]?.focus() } else if (event.key === "ArrowLeft" && index > 0) { this.codeInputs[index - 1]?.focus() } else if (event.key === "ArrowRight" && index < 5) { this.codeInputs[index + 1]?.focus() } },
		onCodePaste (index, event) { this.fillCode(index, event.clipboardData.getData("text")) },
		fillCode (index, value) { const characters = value.replace(/\s/g, "").toUpperCase().slice(0, 6 - index); characters.split("").forEach((char, offset) => { this.code[index + offset] = char }); this.$nextTick(() => { this.codeInputs[Math.min(index + characters.length, 5)]?.focus(); this.submitCodeIfComplete() }) },
		submitCodeIfComplete () { if (!this.loading && this.code.every(character => character.length === 1)) { this.submitCode() } }
	}
}
</script>

<style scoped>
.step-enter-active, .step-leave-active { transition: opacity 180ms ease, transform 180ms ease; }
.step-enter-from { opacity: 0; transform: translateX(12px); }
.step-leave-to { opacity: 0; transform: translateX(-12px); }
</style>
